import {
    SetProjectArchivedDocument,
    SoftDeleteProjectDocument,
    UndoMyProjectShareDocument,
} from '@/graphql/types';
import type { ProjectOverviewRow } from '../../types';
import type { OptionsMenuItem } from '../../components/OptionsMenu';
import { PERMISSION } from '../../../sharing/utils';
import { createEditUrl } from '../../../measuringPoint/utils';
import { getNewMeasuringPointUrl } from '../measuringPoints';
import { createAttributesOverviewPath } from '../../../attributes/routeUtils';
import {
    buildArchiveMenuSpec,
    buildDeleteMenuSpec,
    buildMenuItems,
    buildUndoSharingMenuSpec,
    copyLabel,
    getOverviewRefetchQueriesForProject,
    handleCopyAction,
} from './menuItemActions';
import { ObjectType } from '../../../../../utils/objectType';

const PROJECT = ObjectType.PROJECT.type;
const MEASURING_POINT = ObjectType.MEASURING_POINT.type;

export function createProjectMenuItems(
    project: ProjectOverviewRow,
    {
        showProjectAttributes,
        isArchivedView,
    }: { showProjectAttributes: boolean; isArchivedView: boolean }
): OptionsMenuItem[] {
    const { projectId, projectName, swarmType, permission } = project;
    const newMeasuringPointUrl = getNewMeasuringPointUrl(project);
    // Archiving or deleting a project can stop halfway, with some of its measuring points changed.
    const refetchQueriesOnFailure = getOverviewRefetchQueriesForProject(projectId, isArchivedView);

    const menuSpecs = [
        {
            label: gettext('CONFIGURATION'),
            to: createEditUrl(projectId, swarmType, true),
            requiredPermissions: PERMISSION.FullAccess,
        },
        ...(newMeasuringPointUrl
            ? [
                  {
                      label: gettext('CREATE_MEASURING_POINT'),
                      to: newMeasuringPointUrl,
                      openInNewTab: true,
                      requiredPermissions: PERMISSION.FullAccess,
                  },
              ]
            : []),
        {
            label: gettext('REPORTS'),
            href: `/project/edit_report/${projectId}`,
            requiredPermissions: PERMISSION.FullAccess,
        },
        ...(showProjectAttributes
            ? [
                  {
                      label: gettext('ATTRIBUTES'),
                      to: createAttributesOverviewPath('project', projectId),
                      requiredPermissions: PERMISSION.FullAccess,
                  },
              ]
            : []),
        buildArchiveMenuSpec({
            type: PROJECT,
            id: projectId,
            objectName: projectName,
            archived: project.archived,
            requiredPermissions: PERMISSION.FullAccess,
            mutation: SetProjectArchivedDocument,
            refetchQueriesOnFailure,
        }),
        buildDeleteMenuSpec({
            type: PROJECT,
            id: projectId,
            objectName: projectName,
            requiredPermissions: PERMISSION.FullAccess,
            mutation: SoftDeleteProjectDocument,
            refetchQueriesOnFailure,
        }),
        {
            label: gettext('SHARE_PROJECT'),
            to: `/sharing/share-project-edit/${projectId}`,
            requiredPermissions: PERMISSION.FullAccess,
        },
        {
            label: copyLabel(MEASURING_POINT),
            requiredPermissions: PERMISSION.FullAccess,
            onClick: () =>
                handleCopyAction({
                    id: projectId,
                    swarmType,
                    source: PROJECT,
                    destination: MEASURING_POINT,
                }),
        },
        {
            label: copyLabel(PROJECT),
            requiredPermissions: PERMISSION.FullAccess,
            onClick: () =>
                handleCopyAction({
                    id: projectId,
                    swarmType,
                    source: PROJECT,
                    destination: PROJECT,
                }),
        },
        buildUndoSharingMenuSpec({
            type: PROJECT,
            id: projectId,
            objectName: projectName,
            requiredPermissions: PERMISSION.View,
            mutation: UndoMyProjectShareDocument,
            // Debug-only context for internal warning logs (not user-facing UI text).
            actionLabel: 'undo project sharing',
            variablesFromNumericId: (id) => ({ projectId: id }),
        }),
    ];

    return buildMenuItems(menuSpecs, permission);
}
